import React, { useMemo } from 'react';
import { Box, Divider, useTheme } from '@mui/material';
import {
  getRtlFlipStyle,
  ZigButton,
  ZigPriceLabel,
  ZigTypography,
} from '@zignaly-open/ui';
import { ZigCaretRightIcon } from '@zignaly-open/ui/icons';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import { ROUTE_DASHBOARD, ROUTE_MAIN_MARKETPLACE } from '../../../../routes';
import { useActiveExchange } from 'apis/user/use';
import { BalanceStatus } from './types';
import { useOpenDepositModal } from 'views/Dashboard/components/ManageInvestmentModals/DepositModal';
import { ButtonWrapper } from './styles';
import { useCoinBalances } from '../../../../apis/coin/use';

const BalanceButton = () => {
  const { t } = useTranslation('common');
  const exchange = useActiveExchange();
  const openDepositModal = useOpenDepositModal();
  const { data: balance } = useCoinBalances({
    pollingInterval: 60_000,
  });

  const theme = useTheme();

  const investedAmount = balance?.USDT?.balanceLocked || 0;

  const balanceStatus = useMemo(() => {
    if (!balance) return null;

    if (!investedAmount) {
      return +balance.USDT.balanceFree
        ? BalanceStatus.NoInvestments
        : BalanceStatus.NoFunds;
    }

    return BalanceStatus.Invested;
  }, [investedAmount, balance]);

  if (!balanceStatus) return null;

  const linkWrap = (v: React.ReactElement) =>
    balanceStatus === BalanceStatus.NoFunds ? (
      <div onClick={() => openDepositModal()}>{v}</div>
    ) : (
      <Link
        to={generatePath(
          balanceStatus === BalanceStatus.NoInvestments
            ? ROUTE_MAIN_MARKETPLACE
            : ROUTE_DASHBOARD,
        )}
      >
        {v}
      </Link>
    );

  if (!exchange) return null;

  return linkWrap(
    <ButtonWrapper>
      <ZigButton
        id='menu__balance-link'
        sx={{
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          px: 1,
          py: 0.5,
        }}
        variant='outlined'
      >
        <Box display='flex' alignItems='center'>
          <Box
            sx={{
              display: 'flex',
              ml: 1,
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Box
              gap={1}
              display='flex'
              justifyContent='space-between'
              alignItems='center'
            >
              <ZigTypography
                variant='body2'
                color='neutral300'
                fontSize='12px'
                lineHeight='16px'
                id='top-widget__portolio-label'
              >
                {t('balance.portfolio')}
              </ZigTypography>
              <ZigPriceLabel
                id='top-widget__portolio-amount'
                usd
                value={investedAmount}
                color='neutral100'
                variant='body2'
                fontSize='12px'
                lineHeight='16px'
                fontWeight={400}
              />
            </Box>
            <Box
              gap={1}
              display='flex'
              justifyContent='space-between'
              alignItems='center'
            >
              <ZigTypography
                variant='body2'
                color='neutral300'
                fontSize='11px'
                lineHeight='16px'
                id='top-widget__available-label'
              >
                {t('balance.available')}
              </ZigTypography>
              <ZigPriceLabel
                id='top-widget__available-amount'
                usd
                value={balance?.USDT?.balanceFree}
                color='neutral100'
                variant='body2'
                fontSize='11px'
                lineHeight='16px'
                fontWeight={400}
              />
            </Box>
          </Box>
          <Divider
            variant='middle'
            orientation='vertical'
            sx={{
              borderColor: (muiTheme) => muiTheme.palette.neutral600,
              borderStyle: 'dotted',
              mx: 1.5,
              my: '7px',
            }}
            flexItem
          />
          <Box
            display='flex'
            sx={{
              maxWidth: 113,
            }}
            alignItems='center'
          >
            <ZigTypography
              variant='body2'
              textAlign='center'
              color='neutral300'
              fontSize='11px'
              lineHeight='16px'
              sx={{ mr: 0.5 }}
              id='top-widget__link'
            >
              {balanceStatus === BalanceStatus.NoFunds
                ? t('balance.deposit-funds')
                : balanceStatus === BalanceStatus.NoInvestments
                  ? t('balance.find-traders')
                  : t('balance.my-portfolio')}
            </ZigTypography>
            <ZigCaretRightIcon
              style={getRtlFlipStyle()}
              color={theme.palette.neutral300}
              width={12}
              height={12}
            />
          </Box>
        </Box>
      </ZigButton>
    </ButtonWrapper>,
  );
};

export default BalanceButton;
